// src/pages/VolunteerSettings.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config.js';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { format } from 'date-fns';

// Helper function to format phone number for display
const formatPhoneForDisplay = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  } else if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
};

// Helper function to format phone number to E.164 for storage
const formatPhoneToE164 = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `+1${digits}`;
  } else if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  return phone;
};

// Helper function to validate phone number
const isValidPhoneNumber = (phone) => {
  const digits = phone.replace(/\D/g, '');
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
};

const VolunteerSettings = ({ currentUser }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('notifications');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // SMS-specific state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [volunteerDocId, setVolunteerDocId] = useState(null);
  
  // State for all preferences
  const [preferences, setPreferences] = useState({
    channels: {
      email: {
        enabled: true,
        address: currentUser?.email || '',
        verified: true
      },
      sms: {
        enabled: false,
        number: '',
        verified: false
      }
    },
    notifications: {
      newOpportunities: {
        enabled: true,
        channels: ['email'],
        frequency: 'immediate'
      },
      reminders: {
        enabled: true,
        channels: ['email'],
        timing: [24, 2] // hours before event
      },
      cancellations: {
        enabled: true,
        channels: ['email'],
        frequency: 'immediate'
      },
      scheduleChanges: {
        enabled: true,
        channels: ['email'],
        frequency: 'immediate'
      },
      thankYou: {
        enabled: true,
        channels: ['email'],
        frequency: 'immediate'
      }
    },
    filters: {
      eventTypes: [],
      skills: [],
      minNoticeHours: 24,
      maxEventsPerWeek: 0, // 0 means unlimited
    },
    availability: {
      monday: { morning: false, afternoon: false, evening: false },
      tuesday: { morning: false, afternoon: false, evening: false },
      wednesday: { morning: false, afternoon: false, evening: false },
      thursday: { morning: false, afternoon: false, evening: false },
      friday: { morning: false, afternoon: false, evening: false },
      saturday: { morning: false, afternoon: false, evening: false },
      sunday: { morning: false, afternoon: false, evening: false }
    },
    digest: {
      enabled: false,
      frequency: 'weekly',
      dayOfWeek: 1, // Monday
      timeOfDay: '09:00'
    }
  });

  // Available event types and skills (these would come from your tenant configuration)
  const [availableEventTypes, setAvailableEventTypes] = useState([
    'Food Pantry',
    'Tutoring',
    'Mentoring',
    'Administrative',
    'Event Support',
    'Fundraising',
    'Teaching Assistant'
  ]);

  const [availableSkills, setAvailableSkills] = useState([
    'Spanish Speaking',
    'French Speaking',
    'Math Tutoring',
    'Reading Support',
    'Special Education',
    'Technology',
    'Music',
    'Art',
    'Sports Coaching'
  ]);

  // Load existing preferences
  useEffect(() => {
    loadPreferences();
    loadVolunteerSmsSettings();
    loadTenantOptions();
  }, [currentUser]);

  const loadPreferences = async () => {
    if (!currentUser?.uid) return;

    try {
      const prefDoc = await getDoc(doc(db, 'volunteerPreferences', currentUser.uid));
      if (prefDoc.exists()) {
        setPreferences(prev => ({
          ...prev,
          ...prefDoc.data()
        }));
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load SMS settings from volunteers collection
  const loadVolunteerSmsSettings = async () => {
    if (!currentUser?.tenantId || !currentUser?.email) return;

    try {
      // Find volunteer document by email
      const volunteerQuery = query(
        collection(db, 'volunteers'),
        where('tenantId', '==', currentUser.tenantId),
        where('email', '==', currentUser.email.toLowerCase())
      );
      const volunteerSnapshot = await getDocs(volunteerQuery);
      
      if (!volunteerSnapshot.empty) {
        const volunteerDoc = volunteerSnapshot.docs[0];
        const volunteerData = volunteerDoc.data();
        
        setVolunteerDocId(volunteerDoc.id);
        
        // Load phone number (check multiple field names)
        const existingPhone = volunteerData.phoneNumber || volunteerData.mobileNumber || volunteerData.phone || '';
        setPhoneNumber(formatPhoneForDisplay(existingPhone));
        
        // Load SMS opt-in status
        setSmsOptIn(volunteerData.smsOptIn === true);
        
        // Consider phone "verified" if it exists and is valid
        setPhoneVerified(existingPhone && isValidPhoneNumber(existingPhone));
        
        // Update preferences state to reflect SMS status
        setPreferences(prev => ({
          ...prev,
          channels: {
            ...prev.channels,
            sms: {
              enabled: volunteerData.smsOptIn === true,
              number: formatPhoneForDisplay(existingPhone),
              verified: existingPhone && isValidPhoneNumber(existingPhone)
            }
          }
        }));
      }
    } catch (error) {
      console.error('Error loading volunteer SMS settings:', error);
    }
  };

  const loadTenantOptions = async () => {
    if (!currentUser?.tenantId) return;

    try {
      // Load event types from tenant configuration
      const configQuery = query(
        collection(db, 'tenantConfigurations'),
        where('tenantId', '==', currentUser.tenantId),
        where('configKey', '==', 'volunteer_event_types')
      );
      const configSnapshot = await getDocs(configQuery);
      
      if (!configSnapshot.empty) {
        const config = configSnapshot.docs[0].data();
        if (config.configValue?.types) {
          setAvailableEventTypes(config.configValue.types);
        }
      }
    } catch (error) {
      console.error('Error loading tenant options:', error);
    }
  };

  // Handle SMS toggle
  const handleSmsToggle = () => {
    const newEnabled = !preferences.channels.sms.enabled;
    
    // If enabling SMS, check if phone number exists
    if (newEnabled && !phoneNumber) {
      setErrorMessage('Please enter a phone number to enable SMS notifications.');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    
    // If enabling SMS, validate phone number
    if (newEnabled && !isValidPhoneNumber(phoneNumber)) {
      setErrorMessage('Please enter a valid 10-digit phone number.');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    
    setSmsOptIn(newEnabled);
    setPreferences(prev => ({
      ...prev,
      channels: {
        ...prev.channels,
        sms: {
          ...prev.channels.sms,
          enabled: newEnabled
        }
      }
    }));
  };

  // Handle phone number change
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setPhoneNumber(value);
    
    // Update verified status based on validity
    const isValid = isValidPhoneNumber(value);
    setPhoneVerified(isValid);
    
    setPreferences(prev => ({
      ...prev,
      channels: {
        ...prev.channels,
        sms: {
          ...prev.channels.sms,
          number: value,
          verified: isValid
        }
      }
    }));
  };

  // Handle preference updates
  const handleChannelToggle = (channel) => {
    if (channel === 'sms') {
      handleSmsToggle();
      return;
    }
    
    setPreferences(prev => ({
      ...prev,
      channels: {
        ...prev.channels,
        [channel]: {
          ...prev.channels[channel],
          enabled: !prev.channels[channel].enabled
        }
      }
    }));
  };

  const handleNotificationToggle = (notificationType) => {
    setPreferences(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [notificationType]: {
          ...prev.notifications[notificationType],
          enabled: !prev.notifications[notificationType].enabled
        }
      }
    }));
  };

  const handleNotificationChannelToggle = (notificationType, channel) => {
    // If trying to enable SMS for a notification type, make sure SMS is set up
    if (channel === 'sms' && !preferences.channels.sms.enabled) {
      setErrorMessage('Please enable SMS notifications first and add your phone number.');
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    
    setPreferences(prev => {
      const currentChannels = prev.notifications[notificationType].channels || [];
      const newChannels = currentChannels.includes(channel)
        ? currentChannels.filter(c => c !== channel)
        : [...currentChannels, channel];
      
      return {
        ...prev,
        notifications: {
          ...prev.notifications,
          [notificationType]: {
            ...prev.notifications[notificationType],
            channels: newChannels
          }
        }
      };
    });
  };

  const handleAvailabilityToggle = (day, timeSlot) => {
    setPreferences(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          [timeSlot]: !prev.availability[day][timeSlot]
        }
      }
    }));
  };

  const handleEventTypeToggle = (eventType) => {
    setPreferences(prev => {
      const currentTypes = prev.filters.eventTypes || [];
      const newTypes = currentTypes.includes(eventType)
        ? currentTypes.filter(t => t !== eventType)
        : [...currentTypes, eventType];
      
      return {
        ...prev,
        filters: {
          ...prev.filters,
          eventTypes: newTypes
        }
      };
    });
  };

  const handleSkillToggle = (skill) => {
    setPreferences(prev => {
      const currentSkills = prev.filters.skills || [];
      const newSkills = currentSkills.includes(skill)
        ? currentSkills.filter(s => s !== skill)
        : [...currentSkills, skill];
      
      return {
        ...prev,
        filters: {
          ...prev.filters,
          skills: newSkills
        }
      };
    });
  };

  // Save preferences
const savePreferences = async () => {
    if (!currentUser?.uid) return;
    
    setSaving(true);
    setSuccessMessage('');
    setErrorMessage('');
    
    try {
      // Validate phone number if SMS is enabled
      if (preferences.channels.sms.enabled) {
        if (!phoneNumber) {
          setErrorMessage('Please enter a phone number for SMS notifications.');
          setSaving(false);
          return;
        }
        if (!isValidPhoneNumber(phoneNumber)) {
          setErrorMessage('Please enter a valid 10-digit phone number.');
          setSaving(false);
          return;
        }
      }
      
      // Save to volunteerPreferences collection
      const prefData = {
        ...preferences,
        userId: currentUser.uid,
        tenantId: currentUser.tenantId,
        email: currentUser.email,
        updatedAt: new Date().toISOString()
      };

      await setDoc(doc(db, 'volunteerPreferences', currentUser.uid), prefData, { merge: true });
      
      // Format phone number BEFORE the if/else so it's available in both branches
      const e164Phone = formatPhoneToE164(phoneNumber);
      
      // IMPORTANT: Also save SMS settings to volunteers collection
      // This is where sendSmsIfOptedIn looks for the data
      if (volunteerDocId) {
        await updateDoc(doc(db, 'volunteers', volunteerDocId), {
          phoneNumber: e164Phone,
          smsOptIn: preferences.channels.sms.enabled,
          smsOptInDate: preferences.channels.sms.enabled ? new Date().toISOString() : null,
          smsUpdatedAt: new Date().toISOString()
        });
        
        console.log(`Updated volunteer SMS settings: phone=${e164Phone}, smsOptIn=${preferences.channels.sms.enabled}`);
      } else {
        // No volunteer document exists - CREATE ONE
        console.log('No volunteer document found - creating one now');
        
        const newVolunteerData = {
          email: currentUser.email?.toLowerCase(),
          uid: currentUser.uid,
          tenantId: currentUser.tenantId,
          name: currentUser.displayName || currentUser.name || '',
          phoneNumber: e164Phone,
          smsOptIn: preferences.channels.sms.enabled,
          smsOptInDate: preferences.channels.sms.enabled ? new Date().toISOString() : null,
          smsUpdatedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          status: 'approved'
        };
        
        const newDocRef = doc(db, 'volunteers', currentUser.uid);
        await setDoc(newDocRef, newVolunteerData, { merge: true });
        
        // Update local state so future saves use updateDoc
        setVolunteerDocId(currentUser.uid);
        
        console.log(`Created new volunteer document with uid ${currentUser.uid}:`, newVolunteerData);
      }
      
      setSuccessMessage('Settings saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving preferences:', error);
      setErrorMessage('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        Loading settings...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1000px', margin: '20px auto', padding: '0 15px' }}>
      <h1>Volunteer Settings</h1>
      
      {/* Success Message */}
      {successMessage && (
        <div style={{
          backgroundColor: '#d4edda',
          color: '#155724',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #c3e6cb'
        }}>
          {successMessage}
        </div>
      )}
      
      {/* Error Message */}
      {errorMessage && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #f5c6cb'
        }}>
          {errorMessage}
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        borderBottom: '2px solid #dee2e6',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '5px'
      }}>
        {['notifications', 'availability', 'preferences', 'digest'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '10px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === tab ? '3px solid #007bff' : 'none',
              color: activeTab === tab ? '#007bff' : '#6c757d',
              fontWeight: activeTab === tab ? 'bold' : 'normal',
              textTransform: 'capitalize'
            }}
          >
            {tab === 'digest' ? 'Email Digest' : tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
        
        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div>
            <h2>Notification Preferences</h2>
            <p style={{ color: '#6c757d', marginBottom: '20px' }}>
              Choose how you want to receive notifications about volunteer opportunities and updates.
            </p>

            {/* Communication Channels */}
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ fontSize: '1.1em', marginBottom: '15px' }}>Communication Channels</h3>
              
              {/* Email Channel */}
              <div style={{
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '10px'
              }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={preferences.channels.email.enabled}
                    onChange={() => handleChannelToggle('email')}
                    style={{ marginRight: '10px' }}
                  />
                  <strong>Email Notifications</strong>
                </label>
                {preferences.channels.email.enabled && (
                  <div style={{ marginTop: '10px', marginLeft: '25px' }}>
                    <input
                      type="email"
                      value={preferences.channels.email.address}
                      onChange={(e) => setPreferences(prev => ({
                        ...prev,
                        channels: {
                          ...prev.channels,
                          email: {
                            ...prev.channels.email,
                            address: e.target.value
                          }
                        }
                      }))}
                      placeholder="your.email@example.com"
                      style={{
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #ced4da',
                        width: '300px'
                      }}
                    />
                    {preferences.channels.email.verified && (
                      <span style={{ marginLeft: '10px', color: '#28a745' }}>✓ Verified</span>
                    )}
                  </div>
                )}
              </div>

              {/* SMS Channel - NOW FUNCTIONAL */}
              <div style={{
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '10px'
              }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={preferences.channels.sms.enabled}
                    onChange={() => handleChannelToggle('sms')}
                    style={{ marginRight: '10px' }}
                  />
                  <strong>SMS/Text Notifications</strong>
                </label>
                
                {/* Phone Number Input - Always visible so user can enter number */}
                <div style={{ marginTop: '10px', marginLeft: '25px' }}>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder="(555) 123-4567"
                    style={{
                      padding: '8px',
                      borderRadius: '4px',
                      border: `1px solid ${phoneNumber && !isValidPhoneNumber(phoneNumber) ? '#dc3545' : '#ced4da'}`,
                      width: '300px'
                    }}
                  />
                  {phoneNumber && isValidPhoneNumber(phoneNumber) && (
                    <span style={{ marginLeft: '10px', color: '#28a745' }}>✓ Valid</span>
                  )}
                  {phoneNumber && !isValidPhoneNumber(phoneNumber) && (
                    <span style={{ marginLeft: '10px', color: '#dc3545' }}>Invalid format</span>
                  )}
                  
                  <div style={{ marginTop: '8px', fontSize: '0.85em', color: '#6c757d' }}>
                    Enter your mobile number to receive text message notifications. 
                    Standard message rates may apply. Reply STOP to opt out anytime.
                  </div>
                  
                  {preferences.channels.sms.enabled && phoneNumber && isValidPhoneNumber(phoneNumber) && (
                    <div style={{ 
                      marginTop: '10px', 
                      padding: '10px', 
                      backgroundColor: '#d4edda', 
                      borderRadius: '4px',
                      fontSize: '0.9em',
                      color: '#155724'
                    }}>
                      ✓ SMS notifications are enabled! You'll receive text messages for volunteer events.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Notification Types */}
            <div>
              <h3 style={{ fontSize: '1.1em', marginBottom: '15px' }}>Notification Types</h3>
              
              {Object.entries({
                newOpportunities: {
                  label: 'New Volunteer Opportunities',
                  description: 'Get notified when new volunteer events matching your preferences are posted'
                },
                reminders: {
                  label: 'Event Reminders',
                  description: 'Receive reminders before your scheduled volunteer events'
                },
                cancellations: {
                  label: 'Cancellations',
                  description: 'Be notified immediately if an event you signed up for is cancelled'
                },
                scheduleChanges: {
                  label: 'Schedule Changes',
                  description: 'Get updates about time or location changes for your events'
                },
                thankYou: {
                  label: 'Thank You Messages',
                  description: 'Receive appreciation messages after completing volunteer events'
                }
              }).map(([key, info]) => (
                <div key={key} style={{
                  padding: '15px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  marginBottom: '10px'
                }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={preferences.notifications[key].enabled}
                      onChange={() => handleNotificationToggle(key)}
                      style={{ marginRight: '10px', marginTop: '2px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <strong>{info.label}</strong>
                      <div style={{ fontSize: '0.9em', color: '#6c757d', marginTop: '2px' }}>
                        {info.description}
                      </div>
                    </div>
                  </label>
                  
                  {preferences.notifications[key].enabled && (
                    <div style={{ marginTop: '10px', marginLeft: '25px' }}>
                      <span style={{ marginRight: '15px', fontSize: '0.9em' }}>Send via:</span>
                      <label style={{ marginRight: '15px' }}>
                        <input
                          type="checkbox"
                          checked={preferences.notifications[key].channels?.includes('email')}
                          onChange={() => handleNotificationChannelToggle(key, 'email')}
                          disabled={!preferences.channels.email.enabled}
                        /> Email
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={preferences.notifications[key].channels?.includes('sms')}
                          onChange={() => handleNotificationChannelToggle(key, 'sms')}
                          disabled={!preferences.channels.sms.enabled || !phoneNumber || !isValidPhoneNumber(phoneNumber)}
                        /> SMS
                        {(!preferences.channels.sms.enabled || !phoneNumber) && (
                          <span style={{ marginLeft: '5px', fontSize: '0.8em', color: '#6c757d' }}>
                            (add phone number first)
                          </span>
                        )}
                      </label>
                      
                      {key === 'reminders' && (
                        <div style={{ marginTop: '10px' }}>
                          <span style={{ fontSize: '0.9em' }}>Remind me: </span>
                          <select
                            value={preferences.notifications.reminders.timing?.[0] || 24}
                            onChange={(e) => setPreferences(prev => ({
                              ...prev,
                              notifications: {
                                ...prev.notifications,
                                reminders: {
                                  ...prev.notifications.reminders,
                                  timing: [parseInt(e.target.value), prev.notifications.reminders.timing?.[1] || 2]
                                }
                              }
                            }))}
                            style={{ margin: '0 5px', padding: '4px' }}
                          >
                            <option value="48">48 hours</option>
                            <option value="24">24 hours</option>
                            <option value="12">12 hours</option>
                            <option value="6">6 hours</option>
                          </select>
                          and
                          <select
                            value={preferences.notifications.reminders.timing?.[1] || 2}
                            onChange={(e) => setPreferences(prev => ({
                              ...prev,
                              notifications: {
                                ...prev.notifications,
                                reminders: {
                                  ...prev.notifications.reminders,
                                  timing: [prev.notifications.reminders.timing?.[0] || 24, parseInt(e.target.value)]
                                }
                              }
                            }))}
                            style={{ margin: '0 5px', padding: '4px' }}
                          >
                            <option value="0">No second reminder</option>
                            <option value="2">2 hours</option>
                            <option value="1">1 hour</option>
                            <option value="0.5">30 minutes</option>
                          </select>
                          before the event
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Availability Tab */}
        {activeTab === 'availability' && (
          <div>
            <h2>Availability</h2>
            <p style={{ color: '#6c757d', marginBottom: '20px' }}>
              Let us know when you're typically available to volunteer. We'll prioritize showing you events during these times.
            </p>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Day</th>
                    <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                      Morning<br /><span style={{ fontSize: '0.8em', fontWeight: 'normal' }}>(6am-12pm)</span>
                    </th>
                    <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                      Afternoon<br /><span style={{ fontSize: '0.8em', fontWeight: 'normal' }}>(12pm-5pm)</span>
                    </th>
                    <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>
                      Evening<br /><span style={{ fontSize: '0.8em', fontWeight: 'normal' }}>(5pm-9pm)</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                    <tr key={day} style={{ borderBottom: '1px solid #dee2e6' }}>
                      <td style={{ padding: '10px', textTransform: 'capitalize', fontWeight: '500' }}>
                        {day}
                      </td>
                      {['morning', 'afternoon', 'evening'].map(timeSlot => (
                        <td key={timeSlot} style={{ padding: '10px', textAlign: 'center' }}>
                          <button
                            onClick={() => handleAvailabilityToggle(day, timeSlot)}
                            style={{
                              width: '60px',
                              height: '30px',
                              borderRadius: '15px',
                              border: 'none',
                              cursor: 'pointer',
                              backgroundColor: preferences.availability[day][timeSlot] ? '#28a745' : '#e9ecef',
                              color: preferences.availability[day][timeSlot] ? 'white' : '#6c757d',
                              transition: 'all 0.3s'
                            }}
                          >
                            {preferences.availability[day][timeSlot] ? 'Yes' : 'No'}
                          </button>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#e8f4f8', borderRadius: '8px' }}>
              <p style={{ margin: 0, fontSize: '0.9em' }}>
                <strong>💡 Tip:</strong> This helps us send you relevant volunteer opportunities. 
                You can still sign up for events outside your usual availability.
              </p>
            </div>
          </div>
        )}

        {/* Preferences Tab */}
        {activeTab === 'preferences' && (
          <div>
            <h2>Event Preferences</h2>
            <p style={{ color: '#6c757d', marginBottom: '20px' }}>
              Customize what types of volunteer opportunities you're interested in.
            </p>

            {/* Event Types */}
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ fontSize: '1.1em', marginBottom: '15px' }}>Event Types</h3>
              <p style={{ fontSize: '0.9em', color: '#6c757d', marginBottom: '10px' }}>
                Select the types of events you'd like to hear about. Leave all unchecked to receive all opportunities.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {availableEventTypes.map(eventType => (
                  <label
                    key={eventType}
                    style={{
                      padding: '8px 15px',
                      backgroundColor: preferences.filters.eventTypes?.includes(eventType) ? '#007bff' : '#f8f9fa',
                      color: preferences.filters.eventTypes?.includes(eventType) ? 'white' : '#495057',
                      borderRadius: '20px',
                      cursor: 'pointer',
                      border: '1px solid #dee2e6',
                      transition: 'all 0.3s'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={preferences.filters.eventTypes?.includes(eventType)}
                      onChange={() => handleEventTypeToggle(eventType)}
                      style={{ display: 'none' }}
                    />
                    {eventType}
                  </label>
                ))}
              </div>
            </div>

            {/* Skills */}
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ fontSize: '1.1em', marginBottom: '15px' }}>Your Skills</h3>
              <p style={{ fontSize: '0.9em', color: '#6c757d', marginBottom: '10px' }}>
                Select skills you have. We'll notify you about events that need these skills.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {availableSkills.map(skill => (
                  <label
                    key={skill}
                    style={{
                      padding: '8px 15px',
                      backgroundColor: preferences.filters.skills?.includes(skill) ? '#28a745' : '#f8f9fa',
                      color: preferences.filters.skills?.includes(skill) ? 'white' : '#495057',
                      borderRadius: '20px',
                      cursor: 'pointer',
                      border: '1px solid #dee2e6',
                      transition: 'all 0.3s'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={preferences.filters.skills?.includes(skill)}
                      onChange={() => handleSkillToggle(skill)}
                      style={{ display: 'none' }}
                    />
                    {skill}
                  </label>
                ))}
              </div>
            </div>

            {/* Other Preferences */}
            <div>
              <h3 style={{ fontSize: '1.1em', marginBottom: '15px' }}>Additional Preferences</h3>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>
                  Minimum advance notice for events:
                </label>
                <select
                  value={preferences.filters.minNoticeHours}
                  onChange={(e) => setPreferences(prev => ({
                    ...prev,
                    filters: {
                      ...prev.filters,
                      minNoticeHours: parseInt(e.target.value)
                    }
                  }))}
                  style={{
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ced4da',
                    width: '200px'
                  }}
                >
                  <option value="0">No minimum</option>
                  <option value="12">12 hours</option>
                  <option value="24">24 hours</option>
                  <option value="48">2 days</option>
                  <option value="72">3 days</option>
                  <option value="168">1 week</option>
                </select>
                <div style={{ fontSize: '0.9em', color: '#6c757d', marginTop: '5px' }}>
                  Don't notify me about events starting in less than this time
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>
                  Maximum events per week:
                </label>
                <select
                  value={preferences.filters.maxEventsPerWeek}
                  onChange={(e) => setPreferences(prev => ({
                    ...prev,
                    filters: {
                      ...prev.filters,
                      maxEventsPerWeek: parseInt(e.target.value)
                    }
                  }))}
                  style={{
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ced4da',
                    width: '200px'
                  }}
                >
                  <option value="0">Unlimited</option>
                  <option value="1">1 event</option>
                  <option value="2">2 events</option>
                  <option value="3">3 events</option>
                  <option value="5">5 events</option>
                  <option value="10">10 events</option>
                </select>
                <div style={{ fontSize: '0.9em', color: '#6c757d', marginTop: '5px' }}>
                  Limit notifications to avoid overwhelming your inbox
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Digest Tab */}
        {activeTab === 'digest' && (
          <div>
            <h2>Email Digest</h2>
            <p style={{ color: '#6c757d', marginBottom: '20px' }}>
              Instead of immediate notifications, receive a summary of volunteer opportunities on a schedule.
            </p>

            <div style={{
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={preferences.digest.enabled}
                  onChange={() => setPreferences(prev => ({
                    ...prev,
                    digest: {
                      ...prev.digest,
                      enabled: !prev.digest.enabled
                    }
                  }))}
                  style={{ marginRight: '10px' }}
                />
                <strong>Enable Email Digest</strong>
              </label>
            </div>

            {preferences.digest.enabled && (
              <div>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>
                    Frequency:
                  </label>
                  <select
                    value={preferences.digest.frequency}
                    onChange={(e) => setPreferences(prev => ({
                      ...prev,
                      digest: {
                        ...prev.digest,
                        frequency: e.target.value
                      }
                    }))}
                    style={{
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ced4da',
                      width: '200px'
                    }}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Every Two Weeks</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                {preferences.digest.frequency === 'weekly' && (
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px' }}>
                      Day of Week:
                    </label>
                    <select
                      value={preferences.digest.dayOfWeek}
                      onChange={(e) => setPreferences(prev => ({
                        ...prev,
                        digest: {
                          ...prev.digest,
                          dayOfWeek: parseInt(e.target.value)
                        }
                      }))}
                      style={{
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #ced4da',
                        width: '200px'
                      }}
                    >
                      <option value="0">Sunday</option>
                      <option value="1">Monday</option>
                      <option value="2">Tuesday</option>
                      <option value="3">Wednesday</option>
                      <option value="4">Thursday</option>
                      <option value="5">Friday</option>
                      <option value="6">Saturday</option>
                    </select>
                  </div>
                )}

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '5px' }}>
                    Time of Day:
                  </label>
                  <input
                    type="time"
                    value={preferences.digest.timeOfDay}
                    onChange={(e) => setPreferences(prev => ({
                      ...prev,
                      digest: {
                        ...prev.digest,
                        timeOfDay: e.target.value
                      }
                    }))}
                    style={{
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ced4da',
                      width: '200px'
                    }}
                  />
                </div>

                <div style={{
                  padding: '15px',
                  backgroundColor: '#fff3cd',
                  borderRadius: '8px',
                  marginTop: '20px'
                }}>
                  <p style={{ margin: 0, fontSize: '0.9em' }}>
                    <strong>Note:</strong> When digest is enabled, you'll receive a summary email instead of individual notifications 
                    for new opportunities. Critical notifications (cancellations, reminders) will still be sent immediately.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Save Button */}
      <div style={{ marginTop: '20px', textAlign: 'right' }}>
        <button
          onClick={savePreferences}
          disabled={saving}
          style={{
            backgroundColor: saving ? '#6c757d' : '#28a745',
            color: 'white',
            padding: '12px 30px',
            border: 'none',
            borderRadius: '5px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: '1em',
            fontWeight: 'bold'
          }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Privacy Note */}
      <div style={{
        marginTop: '30px',
        padding: '15px',
        backgroundColor: '#e8f4f8',
        borderRadius: '8px',
        fontSize: '0.9em',
        color: '#495057'
      }}>
        <strong>Privacy & Unsubscribe:</strong> You can adjust these settings anytime. 
        To completely stop receiving volunteer notifications, turn off all notification types above. 
        We respect your preferences and will never share your contact information.
        {preferences.channels.sms.enabled && (
          <span> You can also text STOP to our number anytime to opt out of SMS notifications.</span>
        )}
      </div>
    </div>
  );
};

export default VolunteerSettings;